import { X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import type { TagDto } from '../api/tags-api';

interface TagChipProps {
  className?: string;
  onRemove?: () => void;
  tag: Pick<TagDto, 'id' | 'name' | 'color'>;
}

/** Append the 8-bit hex alpha for ~10% opacity. Only valid for #RRGGBB. */
const HEX_ALPHA_10_PERCENT = '1A';

export function TagChip({ tag, onRemove, className }: TagChipProps) {
  const style =
    tag.color && isValidHex6(tag.color)
      ? {
          color: tag.color,
          borderColor: tag.color,
          backgroundColor: `${tag.color}${HEX_ALPHA_10_PERCENT}`,
        }
      : undefined;

  return (
    <Badge style={style} variant="outline" className={`gap-1 text-xs font-medium ${className ?? ''}`}>
      {tag.name}
      {onRemove && (
        <button
          type="button"
          aria-label={`Remove ${tag.name}`}
          className="-mr-1 hover:opacity-70"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}>
          <X className="h-3 w-3" />
        </button>
      )}
    </Badge>
  );
}

/** Validate that a color is in the strict #RRGGBB form. The schema validator
 * already enforces this on write; defensive double-check here so a malformed
 * stored value can't produce CSS like "#3B81A" (silent visual corruption). */
function isValidHex6(color: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(color);
}
